/**
 * CR120: Page Styling Tab
 * 
 * Configuration panel for background, page takeovers, and styling options.
 */

import { useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Image,
  Sparkles,
  Upload,
} from "lucide-react";
import type {
  BackgroundConfig,
  BackgroundType,
  TakeoverConfig,
} from "@/lib/layout-studio/pageStylingTypes";
import {
  DEFAULT_BACKGROUND_CONFIG,
  DEFAULT_TAKEOVER_CONFIG,
  MOCK_TAKEOVER_THEMES,
} from "@/lib/layout-studio/pageStylingTypes";

interface PageStylingTabProps {
  background: BackgroundConfig;
  takeover: TakeoverConfig;
  onBackgroundChange: (config: BackgroundConfig) => void;
  onTakeoverChange: (config: TakeoverConfig) => void;
}

export function PageStylingTab({
  background,
  takeover,
  onBackgroundChange,
  onTakeoverChange,
}: PageStylingTabProps) {
  // ----------------------------------------------------------------
  // BACKGROUND HANDLERS
  // ----------------------------------------------------------------

  const handleBackgroundTypeChange = useCallback((type: BackgroundType) => {
    onBackgroundChange({ ...background, type });
  }, [background, onBackgroundChange]);

  const handleSolidColorChange = useCallback((color: string) => {
    onBackgroundChange({ ...background, solidColor: color });
  }, [background, onBackgroundChange]);

  const handleGradientChange = useCallback((field: "color1" | "color2" | "angle", value: string | number) => {
    const currentGradient = background.gradient || { color1: "0 0% 98%", color2: "220 15% 95%", angle: 135 };
    onBackgroundChange({
      ...background,
      gradient: { ...currentGradient, [field]: value },
    });
  }, [background, onBackgroundChange]);

  const handleImageConfigChange = useCallback((field: keyof NonNullable<BackgroundConfig["image"]>, value: string | number | null) => {
    const currentImage = background.image || { url: null, opacity: 100, blur: 0, overlayColor: "0 0% 0%", overlayOpacity: 0 };
    onBackgroundChange({
      ...background,
      image: { ...currentImage, [field]: value },
    });
  }, [background, onBackgroundChange]);

  // ----------------------------------------------------------------
  // TAKEOVER HANDLERS
  // ----------------------------------------------------------------

  const handleTakeoverThemeChange = useCallback((themeId: string) => {
    onTakeoverChange({ ...takeover, themeId: themeId === "none" ? null : themeId });
  }, [takeover, onTakeoverChange]);

  const handleTakeoverToggle = useCallback((field: keyof Omit<TakeoverConfig, "themeId">, value: boolean) => {
    onTakeoverChange({ ...takeover, [field]: value });
  }, [takeover, onTakeoverChange]);

  // Get current values with defaults
  const bgType = background.type || "solid";
  const solidColor = background.solidColor || "0 0% 98%";
  const gradient = background.gradient || { color1: "0 0% 98%", color2: "220 15% 95%", angle: 135 };
  const imageConfig = background.image || { url: null, opacity: 100, blur: 0, overlayColor: "0 0% 0%", overlayOpacity: 0 };

  return (
    <div className="space-y-4">
      {/* Background Configuration */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Image className="h-4 w-4" />
            Background
          </CardTitle>
          <CardDescription>
            Configure the page background style.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Background Type Selector */}
          <div className="space-y-2">
            <Label className="text-xs">Background Type</Label>
            <RadioGroup
              value={bgType}
              onValueChange={(v) => handleBackgroundTypeChange(v as BackgroundType)}
              className="grid grid-cols-4 gap-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="solid" id="bg-solid" />
                <Label htmlFor="bg-solid" className="text-xs cursor-pointer">Solid</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="gradient" id="bg-gradient" />
                <Label htmlFor="bg-gradient" className="text-xs cursor-pointer">Gradient</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="image" id="bg-image" />
                <Label htmlFor="bg-image" className="text-xs cursor-pointer">Image</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="takeover" id="bg-takeover" />
                <Label htmlFor="bg-takeover" className="text-xs cursor-pointer">Takeover</Label>
              </div>
            </RadioGroup>
          </div>

          <Separator />

          {/* Solid Color Controls */}
          {bgType === "solid" && (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label className="text-xs">Color (HSL)</Label>
                <div className="flex gap-2">
                  <Input
                    value={solidColor}
                    onChange={(e) => handleSolidColorChange(e.target.value)}
                    placeholder="0 0% 98%"
                    className="text-xs h-8"
                  />
                  <div
                    className="w-8 h-8 rounded border flex-shrink-0"
                    style={{ backgroundColor: `hsl(${solidColor})` }}
                  />
                </div>
                <p className="text-[10px] text-muted-foreground">Enter HSL values (e.g., "220 15% 95%")</p>
              </div>
            </div>
          )}

          {/* Gradient Controls */}
          {bgType === "gradient" && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs">Color 1 (HSL)</Label>
                  <div className="flex gap-2">
                    <Input
                      value={gradient.color1}
                      onChange={(e) => handleGradientChange("color1", e.target.value)}
                      placeholder="0 0% 98%"
                      className="text-xs h-8"
                    />
                    <div
                      className="w-8 h-8 rounded border flex-shrink-0"
                      style={{ backgroundColor: `hsl(${gradient.color1})` }}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Color 2 (HSL)</Label>
                  <div className="flex gap-2">
                    <Input
                      value={gradient.color2}
                      onChange={(e) => handleGradientChange("color2", e.target.value)}
                      placeholder="220 15% 95%"
                      className="text-xs h-8"
                    />
                    <div
                      className="w-8 h-8 rounded border flex-shrink-0"
                      style={{ backgroundColor: `hsl(${gradient.color2})` }}
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Angle</Label>
                  <span className="text-xs text-muted-foreground">{gradient.angle}°</span>
                </div>
                <Slider
                  value={[gradient.angle]}
                  onValueChange={([v]) => handleGradientChange("angle", v)}
                  min={0}
                  max={360}
                  step={15}
                  className="w-full"
                />
              </div>
              {/* Preview */}
              <div
                className="h-12 rounded border"
                style={{
                  background: `linear-gradient(${gradient.angle}deg, hsl(${gradient.color1}), hsl(${gradient.color2}))`,
                }}
              />
            </div>
          )}

          {/* Image Controls */}
          {bgType === "image" && (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label className="text-xs">Image URL</Label>
                <div className="flex gap-2">
                  <Input
                    value={imageConfig.url || ""}
                    onChange={(e) => handleImageConfigChange("url", e.target.value || null)}
                    placeholder="https://..."
                    className="text-xs h-8"
                  />
                  <Button variant="outline" size="sm" className="h-8 px-2" disabled>
                    <Upload className="h-3 w-3" />
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground">Upload coming soon. Enter URL for now.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Opacity</Label>
                    <span className="text-xs text-muted-foreground">{imageConfig.opacity}%</span>
                  </div>
                  <Slider
                    value={[imageConfig.opacity]}
                    onValueChange={([v]) => handleImageConfigChange("opacity", v)}
                    min={0}
                    max={100}
                    step={5}
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Blur</Label>
                    <span className="text-xs text-muted-foreground">{imageConfig.blur}px</span>
                  </div>
                  <Slider
                    value={[imageConfig.blur]}
                    onValueChange={([v]) => handleImageConfigChange("blur", v)}
                    min={0}
                    max={20}
                    step={1}
                  />
                </div>
              </div>
              <Separator />
              <div className="space-y-2">
                <Label className="text-xs">Overlay</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-[10px] text-muted-foreground">Color (HSL)</Label>
                    <Input
                      value={imageConfig.overlayColor}
                      onChange={(e) => handleImageConfigChange("overlayColor", e.target.value)}
                      placeholder="0 0% 0%"
                      className="text-xs h-8"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-[10px] text-muted-foreground">Opacity</Label>
                      <span className="text-[10px] text-muted-foreground">{imageConfig.overlayOpacity}%</span>
                    </div>
                    <Slider
                      value={[imageConfig.overlayOpacity]}
                      onValueChange={([v]) => handleImageConfigChange("overlayOpacity", v)}
                      min={0}
                      max={100}
                      step={5}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Takeover Theme Controls */}
          {bgType === "takeover" && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Background will be controlled by the selected takeover theme below.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Page Takeover Configuration */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            Page Takeover
            <Badge variant="outline" className="text-[10px]">Beta</Badge>
          </CardTitle>
          <CardDescription>
            Apply a theme takeover from Theme Labs.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs">Takeover Theme</Label>
            <Select
              value={takeover.themeId || "none"}
              onValueChange={handleTakeoverThemeChange}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select a theme..." />
              </SelectTrigger>
              <SelectContent>
                {MOCK_TAKEOVER_THEMES.map((theme) => (
                  <SelectItem key={theme.id} value={theme.id} className="text-xs">
                    <div className="flex flex-col">
                      <span>{theme.name}</span>
                      <span className="text-[10px] text-muted-foreground">{theme.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {takeover.themeId && (
            <>
              <Separator />
              <div className="space-y-3">
                <Label className="text-xs">Apply To</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs cursor-pointer" htmlFor="apply-hero">Hero Header</Label>
                    <Switch
                      id="apply-hero"
                      checked={takeover.applyToHero}
                      onCheckedChange={(v) => handleTakeoverToggle("applyToHero", v)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs cursor-pointer" htmlFor="apply-bg">Background</Label>
                    <Switch
                      id="apply-bg"
                      checked={takeover.applyToBackground}
                      onCheckedChange={(v) => handleTakeoverToggle("applyToBackground", v)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs cursor-pointer" htmlFor="apply-cards">Cards Accent</Label>
                    <Switch
                      id="apply-cards"
                      checked={takeover.applyToCardsAccent}
                      onCheckedChange={(v) => handleTakeoverToggle("applyToCardsAccent", v)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs cursor-pointer" htmlFor="apply-filters">Filters</Label>
                    <Switch
                      id="apply-filters"
                      checked={takeover.applyToFilters}
                      onCheckedChange={(v) => handleTakeoverToggle("applyToFilters", v)}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
